import React from "react";
import { motion } from "framer-motion";
import Lottie from "lottie-react";

import bankGradeAnimationData from "../animations/bankGradeAnimation.json";
import allCardsAnimationData from "../animations/allCardsAnimation.json";

const slideInLeft = {
    hidden: { opacity: 0, y: 28 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const },
    },
};

const slideInRight = {
    hidden: { opacity: 0, y: 28 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const },
    },
};

const container = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.12 },
    },
};

const HomeFeatureCards: React.FC = () => {
    return (
        <div className="pt-6 md:pt-8 lg:pt-8 pb-6 md:pb-8 lg:pb-10">
            <motion.div
                variants={container}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
                className="max-w-7xl mx-auto px-4 md:px-5 grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 lg:gap-5"
            >
                {/* Bank-Grade Security */}
                <motion.article
                    variants={slideInLeft}
                    className="group relative overflow-hidden rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-[#ecfdf7] via-white to-[#eefbf4] p-5 sm:p-5 md:p-6 shadow-[0_4px_28px_-10px_rgba(11,127,71,0.18)] transition-[box-shadow,transform] duration-300 hover:shadow-[0_12px_40px_-12px_rgba(11,127,71,0.22)] hover:-translate-y-0.5"
                >
                    <div
                        className="pointer-events-none absolute inset-0 opacity-[0.35]"
                        style={{
                            background:
                                "radial-gradient(ellipse 80% 60% at 100% 0%, rgba(11,127,71,0.12) 0%, transparent 55%)",
                        }}
                    />
                    <div className="pointer-events-none absolute -bottom-12 -left-12 h-36 w-36 rounded-full bg-[#0B7F47]/10 blur-3xl" />

                    <div className="relative flex flex-col h-full">
                        <div className="mb-0.5 flex items-center gap-1.5">
                            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#0B7F47]/12 text-[#0B7F47]" aria-hidden>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    <path d="M9 12l2 2 4-4" />
                                </svg>
                            </span>
                            <span className="text-[#0B7F47] text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.16em]">
                                Security
                            </span>
                        </div>

                        <h3 className="font-display-fran font-semibold text-[#030319] text-lg sm:text-xl tracking-[-0.02em] leading-tight mt-0.5 mb-1.5">
                            Bank-Grade Security
                        </h3>
                        <p className="text-[#475467] text-[13px] sm:text-sm leading-snug mb-3 md:mb-4 max-w-md">
                            Advanced encryption, fraud detection, and biometric login keep you safe.
                        </p>

                        <div className="mt-auto relative rounded-lg bg-white/70 backdrop-blur-sm p-2 sm:p-2.5 ring-1 ring-slate-900/[0.06] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
                            <div className="absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-[#0B7F47]/20 to-transparent" />
                            <Lottie
                                animationData={bankGradeAnimationData}
                                loop={true}
                                autoPlay={true}
                                className="mx-auto w-full max-w-[380px] h-[148px] sm:h-[164px] md:h-[176px]"
                            />
                        </div>
                    </div>
                </motion.article>

                {/* All Cards, One App */}
                <motion.article
                    variants={slideInRight}
                    className="group relative overflow-hidden rounded-2xl border border-amber-200/60 bg-gradient-to-br from-[#fffbeb] via-white to-[#f7fee7] p-5 sm:p-5 md:p-6 shadow-[0_4px_28px_-10px_rgba(180,130,20,0.14)] transition-[box-shadow,transform] duration-300 hover:shadow-[0_12px_40px_-12px_rgba(202,138,4,0.2)] hover:-translate-y-0.5"
                >
                    <div
                        className="pointer-events-none absolute inset-0 opacity-40"
                        style={{
                            background:
                                "radial-gradient(ellipse 75% 55% at 0% 0%, rgba(245,158,11,0.14) 0%, transparent 50%)",
                        }}
                    />
                    <div className="pointer-events-none absolute -bottom-10 -right-10 h-36 w-36 rounded-full bg-[#ca8a04]/10 blur-3xl" />

                    <div className="relative flex flex-col h-full">
                        <div className="mb-0.5 flex items-center gap-1.5">
                            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#ca8a04]/14 text-[#a16207]" aria-hidden>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="5" width="20" height="14" rx="2" />
                                    <path d="M2 10h20" />
                                </svg>
                            </span>
                            <span className="text-[#a16207] text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.16em]">
                                Cards
                            </span>
                        </div>

                        <h3 className="font-display-fran font-semibold text-[#030319] text-lg sm:text-xl tracking-[-0.02em] leading-tight mt-0.5 mb-1.5">
                            All Cards, One App
                        </h3>
                        <p className="text-[#475467] text-[13px] sm:text-sm leading-snug mb-3 md:mb-4 max-w-md">
                            Store and manage both physical and virtual cards in one secure place.
                        </p>

                        <div className="mt-auto relative rounded-lg bg-white/70 backdrop-blur-sm p-2 sm:p-2.5 ring-1 ring-slate-900/[0.06] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
                            <div className="absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/30 to-transparent" />
                            <Lottie
                                animationData={allCardsAnimationData}
                                loop={true}
                                autoPlay={true}
                                className="mx-auto w-full max-w-[520px] h-[148px] sm:h-[164px] md:h-[176px]"
                            />
                        </div>
                    </div>
                </motion.article>
            </motion.div>
        </div>
    );
};

export default HomeFeatureCards;
